"use client";

import type { PointerEvent as ReactPointerEvent } from "react";
import type { RhythmGuideGesture } from "../lib/rhythm-guided";

type Props = {
  elapsed: number;
  gestures: RhythmGuideGesture[];
  activeStartTime: number | null;
  armed: boolean;
  countdown: number | null;
  onStart: (pointerId: number) => boolean;
  onEnd: (pointerId: number) => void;
};

const MAX_RENDERED_GESTURES = 96;

export function RhythmGuidedPad({ elapsed, gestures, activeStartTime, armed, countdown, onStart, onEnd }: Props) {
  const active = activeStartTime !== null;
  const visibleGestures = gestures.slice(-MAX_RENDERED_GESTURES);
  const timeline = active ? [...visibleGestures, {
    id: -1,
    sequenceIndex: gestures.length,
    startTime: activeStartTime,
    endTime: Math.max(activeStartTime, elapsed),
    durationMs: Math.max(0, elapsed - activeStartTime) * 1000,
    startBeat: 0,
  }] : visibleGestures;
  const span = Math.max(0.5, elapsed);

  function pointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (onStart(event.pointerId)) event.currentTarget.setPointerCapture(event.pointerId);
  }

  function pointerEnd(event: ReactPointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    onEnd(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  return <section className={`rhythm-guided-live ${armed ? "armed" : "preparing"}`} aria-label="タップしながら録音">
    <div className="rhythm-guided-live-head"><div><span>RHYTHM GUIDED · 音程は声から</span><strong>{gestures.length + (active ? 1 : 0)} NOTE BOUNDARIES</strong></div><small>{armed ? "音が変わるたびTAP · 伸ばす間はHOLD" : "カウント後すぐに最初の音をタップ"}</small></div>
    <div className={`rhythm-guided-arm-overlay ${armed ? "go" : "counting"}`} role="status"><strong>{armed ? "GO" : countdown ?? "…"}</strong><span>{armed ? "TAP INPUT ARMED" : "準備"}</span></div>
    <button
      type="button"
      className={`${active ? "rhythm-guided-pad holding" : "rhythm-guided-pad"} ${armed ? "armed" : "preparing"}`}
      aria-disabled={!armed}
      onPointerDown={pointerDown}
      onPointerUp={pointerEnd}
      onPointerCancel={pointerEnd}
      onContextMenu={(event) => event.preventDefault()}
    >
      <strong>{active ? "HOLD…" : "TAP / HOLD"}</strong>
      <span>{active ? `${Math.round((elapsed - activeStartTime) * 1000)} ms` : armed ? "歌いながら、音の区切りを押す" : "表示したまま待機 · GOで受付開始"}</span>
    </button>
    <div className="rhythm-guided-timeline" aria-label="録音中のタップ境界">
      {timeline.map((gesture) => {
        const left = Math.min(100, gesture.startTime / span * 100);
        const width = Math.max(1.4, (gesture.endTime - gesture.startTime) / span * 100);
        return <i key={gesture.id} className={gesture.id === -1 ? "active" : ""} style={{ left: `${left}%`, width: `${width}%` }} />;
      })}
    </div>
  </section>;
}
